import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../api/axiosConfig';
import './VisitDetailPage.css';

// Détail d'un contrôle terrain. Reprend la mise en page et les classes de VisitDetailPage
// pour être identique au détail d'une visite tel que le voit la hiérarchie.

const PRESENCES = { PRESENT: 'Présent', ABSENT: 'Pas présent', DEJA_PASSE: 'Déjà passé' };
const COHERENCES = { OUI: 'Oui', NON: 'Non', ECART_CONSTATE: 'Écart constaté' };
const APPRECIATIONS = {
  CONFORME: { libelle: 'Conforme', classe: 'valide' },
  A_AMELIORER: { libelle: 'À améliorer', classe: 'pending' },
  NON_CONFORME: { libelle: 'Non conforme', classe: 'rejete' },
  // Avis client
  SATISFAIT: { libelle: 'Satisfait', classe: 'valide' },
  NON_SATISFAIT: { libelle: 'Pas satisfait', classe: 'rejete' },
};

const TYPES_OUTIL = {
  FRIGO: 'Frigo', VITRINE: 'Vitrine', 'PLAQUE BRANDEE': 'Plaque brandée', PARASOL: 'Parasol',
  GLACIERE: 'Glacière', 'ENSEIGNE LUMINEUSE': 'Enseigne lumineuse', 'STOP RAYON': 'Stop rayon',
  ILOT: 'Îlot', 'TETE DE GONDOLE': 'Tête de gondole', AUTRE: 'Autre',
};
const nomOutil = (o) => (o.type === 'AUTRE' && o.precision) ? o.precision : (TYPES_OUTIL[o.type] || o.type);

const ouiNon = (v) => (v === true ? 'Oui' : v === false ? 'Non' : 'N/A');

const duree = (c) => {
  if (!c?.heure_debut || !c?.heure_fin) return 'N/A';
  const [h1, m1] = c.heure_debut.split(':').map(Number);
  const [h2, m2] = c.heure_fin.split(':').map(Number);
  const minutes = (h2 * 60 + m2) - (h1 * 60 + m1);
  return minutes >= 0 ? `${minutes} minutes` : 'N/A';
};

const Badge = ({ cle }) => {
  const a = APPRECIATIONS[cle];
  return a ? <span className={`status-badge ${a.classe}`}>{a.libelle}</span> : 'N/A';
};

const Texte = ({ libelle, valeur }) => valeur ? (
  <div className="detail-item" style={{ marginTop: '15px' }}>
    <p><strong>{libelle} :</strong></p>
    <p className="observation-text">{valeur}</p>
  </div>
) : null;

// Une rubrique de la grille : son appréciation, puis ce que le contrôleur y a noté
const Rubrique = ({ titre, cle, children }) => (
  <section className="detail-section">
    <h2>{titre}</h2>
    <div className="detail-grid">
      <div className="detail-item"><p><strong>Appréciation :</strong> <Badge cle={cle} /></p></div>
    </div>
    {children}
  </section>
);

const Item = ({ libelle, valeur }) => (
  <div className="detail-item">
    <p><strong>{libelle} :</strong> {valeur || 'N/A'}</p>
  </div>
);

function ControleTerrainDetailPage() {
  const { controleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [controle, setControle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    const charger = async () => {
      try {
        const reponse = await axiosInstance.get(`/controles-terrain/${controleId}`);
        setControle(reponse.data);
      } catch (e) {
        setErreur(e.response?.status === 403
          ? "Vous n'avez pas accès à ce contrôle."
          : "Ce contrôle n'a pas pu être chargé.");
      } finally {
        setIsLoading(false);
      }
    };
    charger();
  }, [controleId]);

  // Revenir à la liste d'où l'on vient ; si la page a été ouverte par un lien direct,
  // il n'y a pas de précédent et on remonte à la liste du rôle courant.
  const retour = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      const base = location.pathname.split('/controle-terrain')[0];
      navigate(`${base}/controles-terrain`);
    }
  };

  if (isLoading) return <div className="visit-detail-page"><p>Chargement du contrôle...</p></div>;
  if (erreur) return (
    <div className="visit-detail-page">
      <p className="no-data-message">{erreur}</p>
      <button onClick={retour} className="back-button">Retour à la liste</button>
    </div>
  );

  // Contrôles saisis avant la grille en 5 rubriques : leurs constats oui/non sont
  // encore affichés pour ne pas perdre l'historique
  const ancienneGrille = [controle.rayon_conforme, controle.frigo_conforme, controle.client_satisfait]
    .some(v => v !== null && v !== undefined);

  return (
    <div className="visit-detail-page">
      <header className="detail-header">
        <div>
          <h1>Contrôle chez {controle.client_nom}</h1>
          <p>
            <strong>Date :</strong> {new Date(controle.date_controle).toLocaleDateString()}
            {'  '}<strong>Avis global :</strong> <Badge cle={controle.appreciation} />
          </p>
        </div>
        <button onClick={retour} className="back-button">Retour à la liste</button>
      </header>

      <section className="detail-section">
        <h2>🕒 Passage du contrôleur</h2>
        <div className="detail-grid">
          <Item libelle="Contrôleur" valeur={controle.controleur_nom} />
          <Item libelle="Heure d'arrivée" valeur={controle.heure_debut} />
          <Item libelle="Heure de soumission" valeur={controle.heure_fin} />
          <Item libelle="Durée sur place" valeur={duree(controle)} />
        </div>
      </section>

      <section className="detail-section">
        <h2>📍 Vérification terrain</h2>
        <div className="detail-grid">
          <Item libelle="Présence sur site"
            valeur={controle.est_sur_site ? 'Confirmée' : 'Non confirmée'} />
          <Item libelle="Distance du client"
            valeur={controle.distance_client != null ? `${Math.round(controle.distance_client)} m` : 'N/A'} />
          <Item libelle="Précision du relevé"
            valeur={controle.precision_gps != null ? `± ${Math.round(controle.precision_gps)} m` : 'N/A'} />
          {controle.latitude && controle.longitude && (
            <div className="detail-item">
              <p>
                <strong>Position :</strong>{' '}
                <a href={`https://www.google.com/maps?q=${controle.latitude},${controle.longitude}`}
                   target="_blank" rel="noreferrer">Voir sur Google Maps</a>
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="detail-section">
        <h2>🏪 Point de vente</h2>
        <div className="detail-grid">
          <Item libelle="Client" valeur={controle.client_nom} />
          <Item libelle="Zone" valeur={controle.zone} />
          <Item libelle="Base" valeur={controle.base} />
          {controle.hotesse_nom
            ? <Item libelle="Hôtesse contrôlée" valeur={controle.hotesse_nom} />
            : <Item libelle="Merchandiser du point" valeur={controle.merchandiser_nom} />}
        </div>
      </section>

      <section className="detail-section">
        <h2>👤 {controle.hotesse_nom ? "Présence de l'hôtesse" : 'Présence du merchandiser'}</h2>
        <div className="detail-grid">
          <Item libelle="Constat" valeur={PRESENCES[controle.presence_marchandiser]} />
          {controle.presence_marchandiser === 'DEJA_PASSE' && (
            <Item libelle="Passage confirmé par le client"
              valeur={ouiNon(controle.passage_confirme_client)} />
          )}
        </div>
      </section>

      <Rubrique titre="1. Disponibilité" cle={controle.appreciation_disponibilite}>
        {controle.releves_coherents && (
          <div className="detail-grid" style={{ marginTop: '15px' }}>
            <Item libelle="Relevés déclarés cohérents" valeur={COHERENCES[controle.releves_coherents]} />
          </div>
        )}
        <Texte libelle="Ruptures constatées" valeur={controle.ruptures_constatees} />
        <Texte libelle="Écart constaté" valeur={controle.ecart_constate} />
        <Texte libelle="Commentaire" valeur={controle.commentaire_disponibilite} />
      </Rubrique>

      <Rubrique titre="2. Visibilité" cle={controle.appreciation_visibilite}>
        <Texte libelle="Commentaire" valeur={controle.commentaire_visibilite} />
      </Rubrique>

      <Rubrique titre="3. Outils d'aide à la vente" cle={controle.appreciation_outils_vente}>
        {controle.outils_vente?.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '15px' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                <th style={{ padding: '6px' }}>Outil</th>
                <th style={{ padding: '6px' }}>Appréciation</th>
                <th style={{ padding: '6px' }}>Commentaire</th>
              </tr>
            </thead>
            <tbody>
              {controle.outils_vente.map((o, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '6px' }}>{nomOutil(o)}</td>
                  <td style={{ padding: '6px' }}><Badge cle={o.appreciation} /></td>
                  <td style={{ padding: '6px' }}>{o.commentaire || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Texte libelle="Commentaire" valeur={controle.commentaire_outils_vente} />
      </Rubrique>

      <Rubrique titre="4. Avis client" cle={controle.appreciation_avis_client}>
        <Texte libelle="Remarques du client" valeur={controle.remarques_client} />
      </Rubrique>

      <Rubrique titre="5. Avis global du superviseur" cle={controle.appreciation}>
        <Texte libelle="Observations du contrôleur" valeur={controle.observations} />
      </Rubrique>

      {ancienneGrille && (
        <section className="detail-section">
          <h2>Constats de l'ancienne grille</h2>
          <div className="detail-grid">
            <Item libelle="Rayon conforme au planogramme" valeur={ouiNon(controle.rayon_conforme)} />
            <Item libelle="Frigo propre et correctement rempli" valeur={ouiNon(controle.frigo_conforme)} />
            <Item libelle="Client satisfait du passage" valeur={ouiNon(controle.client_satisfait)} />
          </div>
        </section>
      )}

      {controle.photos?.length > 0 && (
        <section className="detail-section">
          <h2>📸 Photos ({controle.photos.length})</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {controle.photos.map(p => (
              <a key={p.id} href={`${axiosInstance.defaults.baseURL}${p.photo_url}`}
                 target="_blank" rel="noreferrer">
                <img src={`${axiosInstance.defaults.baseURL}${p.photo_url}`}
                  alt="Photo du contrôle"
                  style={{ width: '180px', height: '180px', objectFit: 'cover', borderRadius: '6px' }} />
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default ControleTerrainDetailPage;
